import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Camera, Loader2, FileText, Car as CarIcon, Lock, DollarSign as DollarSignIcon, X, Shield, KeyRound, Plus, Trash2, ShieldCheck, FileBadge2, Wallet, ArrowRight, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { VEHICLE_MAKE_LIST, VEHICLE_MAKES, VEHICLE_YEARS, VEHICLE_COLORS } from "@/lib/vehicleData";
import DriverAttestationCard from "@/components/DriverAttestationCard";
import DeliveryHistoryVault from "@/components/driver/DeliveryHistoryVault";

// Upload limits per file type (driver profile assets stream to MongoDB GridFS via /api/uploads/driver-doc)
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024; // 100 MB

async function uploadDriverDoc(file) {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File too large (max 100 MB · this file is ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
  }
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads/driver-doc", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  // Resolve to an absolute URL so <img src> and window.open work everywhere
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base + data.url;
}

async function uploadDriverDocFull(file) {
  // v2.6.8 batch M · same as uploadDriverDoc but returns full {file_id,url,full_url}
  // so internal-doc attach endpoints (OSBI / Taxi License / Permits) can pass both
  // file_id (server-stored) and the absolute url (frontend display).
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File too large (max 100 MB · this file is ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
  }
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads/driver-doc", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return { file_id: data.file_id, url: data.url, full_url: base + data.url, size: data.size };
}

export default function DriverProfilePage() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("vehicle");
  const fileRef = useRef(null);

  useEffect(() => { api.get("/auth/me").then(r => setMe(r.data)).catch(()=>{}); }, []);
  if (!me) return <Layout title="My Profile"><div className="text-zinc-500">Loading…</div></Layout>;

  const update = async (payload) => {
    try {
      const { data } = await api.patch("/auth/me", payload);
      setMe(data);
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const onPickPicture = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const url = await uploadDriverDoc(f);
      await update({ profile_picture_url: url });
    } catch (err) { toast.error(err.message || "Upload failed"); }
  };

  return (
    <Layout title="My Profile">
      <Card className="bg-zinc-900 border-zinc-800 p-5 mb-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <button data-testid="pic-upload-btn" onClick={() => fileRef.current?.click()} className="h-20 w-20 rounded-full bg-zinc-950 border-2 border-amber-400/40 overflow-hidden flex items-center justify-center text-zinc-500 hover:border-amber-400 transition">
              {me.profile_picture_url ? (
                <img src={me.profile_picture_url} alt="" className="h-full w-full object-cover"/>
              ) : (
                <Camera className="h-6 w-6"/>
              )}
            </button>
            <input data-testid="pic-input" ref={fileRef} type="file" accept="image/*" capture="user" onChange={onPickPicture} className="hidden"/>
            <div className="absolute -bottom-1 -right-1 bg-amber-400 text-zinc-950 text-[9px] font-bold rounded-full px-1.5 py-0.5 uppercase tracking-wider">Edit</div>
          </div>
          <div className="min-w-0">
            <div className="text-lg font-bold truncate">{me.name}</div>
            <div className="text-xs text-zinc-400 truncate">{me.email}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{me.phone || "—"} · {me.role}</div>
            {/* v2.7.7 · Gender-Preferred Dispatch · Driver gender self-identification */}
            <div className="mt-2 flex items-center gap-1.5">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Gender:</label>
              <select
                data-testid="driver-profile-gender"
                value={me.gender || "unspecified"}
                onChange={(e) => update({ gender: e.target.value })}
                className="bg-zinc-950 border border-zinc-700 rounded text-[11px] text-zinc-100 px-2 py-1"
              >
                <option value="unspecified">Unspecified</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>
          {(me.vehicle_make || me.vehicle_model) && (
            <div className="ml-auto bg-zinc-950 border border-amber-400/30 rounded-lg px-3 py-2 text-xs hidden sm:block" data-testid="vehicle-card">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1"><CarIcon className="h-3 w-3"/>Vehicle</div>
              <div className="font-bold">{me.vehicle_year} {me.vehicle_make} {me.vehicle_model}</div>
              <div className="text-zinc-400">{me.vehicle_color || ""} {me.plate_number ? `· ${me.plate_number}` : ""}</div>
            </div>
          )}
        </div>
      </Card>

      {/* v2.6.8 batch M · attestation card directly below the green Verified
          badge in the header — shows OSBI + DMV labels and a "View Taxi
          License" button that opens the per-vehicle license at ~50% viewport. */}
      <DriverAttestationCard driver={me} variant="self"/>

      {/* v2.7.56+E33.3 · Prominent "End of Day Deposit" shortcut. Navigates
          to the dedicated /end-of-day route — a real page transition that
          survives react-router's hash-nav quirk. */}
      <Link to="/end-of-day" data-testid="profile-eod-link">
        <Card className="group bg-zinc-900 hover:bg-zinc-800 border-zinc-800 hover:border-emerald-400/50 ring-1 ring-emerald-400/20 mb-4 p-4 transition-all cursor-pointer">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center text-emerald-300 group-hover:bg-emerald-400 group-hover:text-zinc-950 transition-colors flex-shrink-0">
              <Wallet className="h-5.5 w-5.5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-zinc-100">End of Day Deposit</div>
              <div className="text-xs text-zinc-400">Submit shift totals · cash · Square · Cash App · Venmo · tips</div>
            </div>
            <ArrowRight className="h-5 w-5 text-zinc-600 group-hover:text-emerald-400 transition-colors flex-shrink-0"/>
          </div>
        </Card>
      </Link>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800 grid grid-cols-5 mb-4">
          <TabsTrigger value="vehicle" data-testid="tab-vehicle"><CarIcon className="h-4 w-4 mr-1"/>Vehicle</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments"><DollarSignIcon className="h-4 w-4 mr-1"/>Payment QRs</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs"><Lock className="h-4 w-4 mr-1"/>Internal Docs</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="tab-deliveries"><Package className="h-4 w-4 mr-1"/>Deliveries</TabsTrigger>
          <TabsTrigger value="admin" data-testid="tab-admin"><Shield className="h-4 w-4 mr-1"/>Admin Access</TabsTrigger>
        </TabsList>
        <TabsContent value="vehicle"><VehicleForm me={me} update={update}/></TabsContent>
        <TabsContent value="payments"><PaymentQRForm me={me} update={update}/></TabsContent>
        <TabsContent value="docs"><DocsForm me={me} update={update}/></TabsContent>
        <TabsContent value="deliveries"><DeliveryHistoryVault/></TabsContent>
        <TabsContent value="admin"><AdminAccessSection/></TabsContent>
      </Tabs>
    </Layout>
  );
}

function QRImagePicker({ label, kind, currentUrl, currentHandle, onSave, testidPrefix }) {
  const fileRef = useRef(null);
  const [handle, setHandle] = useState(currentHandle || "");
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const url = await uploadDriverDoc(f);
      await onSave({ [`${kind}_qr_url`]: url });
      toast.success(`${label} QR saved`);
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const removeQR = async () => {
    if (!window.confirm(`Remove ${label} QR?`)) return;
    await onSave({ [`${kind}_qr_url`]: null });
    toast.success(`${label} QR removed`);
  };

  const saveHandle = async () => {
    setBusy(true);
    try { await onSave({ [`${kind}_handle`]: handle.trim() }); toast.success(`${label} handle saved`); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid={`${testidPrefix}-card`}>
      <h3 className="font-bold mb-3 flex items-center gap-2">
        <DollarSignIcon className="h-4 w-4 text-amber-400"/>{label}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">{label} Username / $cashtag</Label>
          <div className="flex gap-2 mt-1">
            <Input data-testid={`${testidPrefix}-handle`} value={handle} onChange={e=>setHandle(e.target.value)} placeholder={kind === "cashapp" ? "$YourTag" : "@your-venmo"} className="bg-zinc-950 border-zinc-700"/>
            <Button data-testid={`${testidPrefix}-save-handle`} onClick={saveHandle} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold flex-shrink-0">SAVE</Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1.5">Riders see this on their booking confirmation so they can pay you directly.</p>

          <div className="mt-4 border-t border-zinc-800 pt-3">
            <Label className="text-xs">Upload your {label} QR Code (open the {label} app → My QR → screenshot or save)</Label>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid={`${testidPrefix}-input`}/>
            <Button data-testid={`${testidPrefix}-upload`} onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="bg-zinc-950 border-zinc-700 mt-2 w-full">
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Camera className="h-4 w-4 mr-1"/>{currentUrl ? "Replace QR" : "Upload QR"}</>}
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-center">
          {currentUrl ? (
            <div className="relative">
              <img data-testid={`${testidPrefix}-image`} src={currentUrl} alt={`${label} QR`} className="h-48 w-48 object-contain rounded bg-white p-2 border-2 border-amber-400/40"/>
              <Button data-testid={`${testidPrefix}-remove`} onClick={removeQR} variant="ghost" size="icon" className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-red-600 hover:bg-red-500 text-white"><X className="h-3.5 w-3.5"/></Button>
            </div>
          ) : (
            <div className="h-48 w-48 rounded bg-zinc-950 border-2 border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 text-xs text-center px-4">No QR uploaded yet</div>
          )}
        </div>
      </div>
    </Card>
  );
}

function PaymentQRForm({ me, update }) {
  return (
    <div className="space-y-4">
      <QRImagePicker label="Cash App" kind="cashapp" currentUrl={me.cashapp_qr_url} currentHandle={me.cashapp_handle} onSave={update} testidPrefix="cashapp"/>
      <QRImagePicker label="Venmo" kind="venmo" currentUrl={me.venmo_qr_url} currentHandle={me.venmo_handle} onSave={update} testidPrefix="venmo"/>
    </div>
  );
}

function VehicleForm({ me, update }) {
  const [form, setForm] = useState({
    vehicle_make: me.vehicle_make || "", vehicle_model: me.vehicle_model || "",
    vehicle_year: me.vehicle_year || "", vehicle_color: me.vehicle_color || "",
    plate_number: me.plate_number || "",
  });
  const [busy, setBusy] = useState(false);
  const models = form.vehicle_make ? (VEHICLE_MAKES[form.vehicle_make] || []) : [];

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await update({ ...form, vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null }); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5">
      <h3 className="font-bold mb-3 flex items-center gap-2"><CarIcon className="h-4 w-4 text-amber-400"/>Vehicle Information</h3>
      <p className="text-xs text-zinc-400 mb-4">This information is shown on your driver profile and on rider waybills for transparency.</p>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Make</Label>
          <Select value={form.vehicle_make} onValueChange={v => setForm({ ...form, vehicle_make: v, vehicle_model: "" })}>
            <SelectTrigger data-testid="veh-make" className="bg-zinc-950 border-zinc-700 mt-1"><SelectValue placeholder="Select make"/></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-60">
              {VEHICLE_MAKE_LIST.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Model</Label>
          <Select value={form.vehicle_model} onValueChange={v => setForm({ ...form, vehicle_model: v })} disabled={!models.length}>
            <SelectTrigger data-testid="veh-model" className="bg-zinc-950 border-zinc-700 mt-1"><SelectValue placeholder={models.length ? "Select model" : "Select make first"}/></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-60">
              {models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Year</Label>
          <Select value={String(form.vehicle_year)} onValueChange={v => setForm({ ...form, vehicle_year: v })}>
            <SelectTrigger data-testid="veh-year" className="bg-zinc-950 border-zinc-700 mt-1"><SelectValue placeholder="Year"/></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-60">
              {VEHICLE_YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Color</Label>
          <Select value={form.vehicle_color} onValueChange={v => setForm({ ...form, vehicle_color: v })}>
            <SelectTrigger data-testid="veh-color" className="bg-zinc-950 border-zinc-700 mt-1"><SelectValue placeholder="Color"/></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-60">
              {VEHICLE_COLORS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">License Plate</Label>
          <Input data-testid="veh-plate" value={form.plate_number} onChange={e=>setForm({...form, plate_number: e.target.value.toUpperCase()})} placeholder="ABC-1234" className="bg-zinc-950 border-zinc-700 mt-1 meter-display"/>
        </div>
        <Button data-testid="veh-save-btn" type="submit" disabled={busy} className="col-span-2 h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SAVE VEHICLE"}</Button>
      </form>
    </Card>
  );
}

function DocsForm({ me, update }) {
  const [form, setForm] = useState({
    insurance_doc_url: me.insurance_doc_url || "",
    registration_doc_url: me.registration_doc_url || "",
    drivers_license_front_url: me.drivers_license_front_url || "",
    drivers_license_back_url: me.drivers_license_back_url || "",
    license_plate_url: me.license_plate_url || "",
  });
  const [busy, setBusy] = useState(false);
  const insRef = useRef(null);
  const regRef = useRef(null);
  const dlFrontRef = useRef(null);
  const dlBackRef = useRef(null);
  const platePhotoRef = useRef(null);

  // v2.6.8 batch M · OSBI background + Taxi License + multi-Permits
  const [osbi, setOsbi] = useState({ file_id: me.osbi_doc_file_id || null, file_url: me.osbi_doc_url || null });
  const [taxiLic, setTaxiLic] = useState({ file_id: me.taxi_license_file_id || null, file_url: me.taxi_license_url || null });
  const [permits, setPermits] = useState(me.taxi_permits || []);
  // v2.6.9 batch U · Service Oklahoma driving record + police auth letters
  const [sokRecords, setSokRecords] = useState(me.sok_driving_records || []);
  const [policeLetters, setPoliceLetters] = useState(me.police_auth_letters || []);
  const osbiRef = useRef(null);
  const taxiLicRef = useRef(null);

  const onPickFile = async (key, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const url = await uploadDriverDoc(f);
      setForm(s => ({ ...s, [key]: url }));
      toast.success("Uploaded · click SAVE DOCUMENTS to keep");
    } catch (err) { toast.error(err.message || "Upload failed"); }
  };

  const onPickInternal = async (kind, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const meta = await uploadDriverDocFull(f);
      const payload = { file_id: meta.file_id, file_url: meta.full_url };
      if (kind === "osbi") {
        await api.post("/driver/internal-docs/osbi", payload);
        setOsbi({ file_id: meta.file_id, file_url: meta.full_url });
        toast.success("OSBI background check uploaded ✓");
      } else if (kind === "taxi_license") {
        await api.post("/driver/internal-docs/taxi-license", payload);
        setTaxiLic({ file_id: meta.file_id, file_url: meta.full_url });
        toast.success("Taxi License uploaded · taxi-permit-photo ✓");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Upload failed"); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await update(form); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-zinc-900 border-zinc-800 p-5">
        <h3 className="font-bold mb-3 flex items-center gap-2"><Lock className="h-4 w-4 text-amber-400"/>Internal Documents</h3>
        <p className="text-xs text-zinc-400 mb-4">For internal Buller Taxi use only. Upload a clear photo or scan. Updates are logged.</p>
        <form onSubmit={submit} className="space-y-4">
          <DocUpload label="Insurance Verification" testid="doc-insurance" current={form.insurance_doc_url} onPick={(e)=>onPickFile("insurance_doc_url", e)} clear={()=>setForm(s=>({...s, insurance_doc_url: ""}))} fileRef={insRef}/>
          <DocUpload label="Vehicle Registration" testid="doc-registration" current={form.registration_doc_url} onPick={(e)=>onPickFile("registration_doc_url", e)} clear={()=>setForm(s=>({...s, registration_doc_url: ""}))} fileRef={regRef}/>
          <DocUpload label="Driver's License · Front" testid="doc-dl-front" current={form.drivers_license_front_url} onPick={(e)=>onPickFile("drivers_license_front_url", e)} clear={()=>setForm(s=>({...s, drivers_license_front_url: ""}))} fileRef={dlFrontRef}/>
          <DocUpload label="Driver's License · Back" testid="doc-dl-back" current={form.drivers_license_back_url} onPick={(e)=>onPickFile("drivers_license_back_url", e)} clear={()=>setForm(s=>({...s, drivers_license_back_url: ""}))} fileRef={dlBackRef}/>
          <DocUpload label="License Plate (vehicle plate photo)" testid="doc-plate" current={form.license_plate_url} onPick={(e)=>onPickFile("license_plate_url", e)} clear={()=>setForm(s=>({...s, license_plate_url: ""}))} fileRef={platePhotoRef}/>
          <Button data-testid="doc-save-btn" type="submit" disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SAVE DOCUMENTS"}</Button>
        </form>
      </Card>

      {/* OSBI Fingerprint Background Check */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="osbi-doc-card">
        <h3 className="font-bold mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-green-400"/>OSBI Fingerprint Background Check</h3>
        <p className="text-[11px] text-zinc-500 mb-3">Required attestation document. Uploads save immediately.</p>
        <div className="flex items-center gap-2">
          <Button type="button" data-testid="osbi-doc-pick" onClick={() => osbiRef.current?.click()} variant="outline" className="bg-zinc-950 border-zinc-700">
            <FileText className="h-4 w-4 mr-1"/>{osbi.file_id ? "Replace" : "Upload"}
          </Button>
          {osbi.file_url && <Button type="button" data-testid="osbi-doc-view" variant="ghost" onClick={() => window.open(osbi.file_url, "_blank")} className="text-amber-400 text-xs">View current</Button>}
          {osbi.file_url && <span className="text-[10px] text-green-400 uppercase">Uploaded ✓</span>}
        </div>
        <input ref={osbiRef} type="file" accept="image/*,application/pdf" onChange={(e)=>onPickInternal("osbi", e)} className="hidden" data-testid="osbi-doc-input"/>
      </Card>

      {/* v2.6.9 batch U · Service Oklahoma Driving Record Verification (max 4) */}
      <SokRecordsCard records={sokRecords} onChange={setSokRecords}/>

      {/* Taxi Cab License (per-vehicle Occupational Tax License) */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="taxi-license-card">
        <h3 className="font-bold mb-1 flex items-center gap-2"><FileBadge2 className="h-4 w-4 text-amber-400"/>Taxi Cab License (Occupational Tax License)</h3>
        <p className="text-[11px] text-zinc-500 mb-1">The per-vehicle license posted on the dashboard (City of Weatherford / County of Custer issuance).</p>
        <p className="text-[10px] text-zinc-600 mb-3">Comment / handle: <span className="text-amber-400 font-mono">taxi-permit-photo</span></p>
        <div className="flex items-center gap-2">
          <Button type="button" data-testid="taxi-lic-pick" onClick={() => taxiLicRef.current?.click()} variant="outline" className="bg-zinc-950 border-zinc-700">
            <FileText className="h-4 w-4 mr-1"/>{taxiLic.file_id ? "Replace" : "Upload"}
          </Button>
          {taxiLic.file_url && <Button type="button" data-testid="taxi-lic-view" variant="ghost" onClick={() => window.open(taxiLic.file_url, "_blank")} className="text-amber-400 text-xs">View current</Button>}
          {taxiLic.file_url && <span className="text-[10px] text-green-400 uppercase">Uploaded ✓</span>}
        </div>
        <input ref={taxiLicRef} type="file" accept="image/*,application/pdf" onChange={(e)=>onPickInternal("taxi_license", e)} className="hidden" data-testid="taxi-lic-input"/>
      </Card>

      {/* v2.6.9 batch U · Police Department Authorization Letters (multi · max 10) */}
      <PoliceAuthCard letters={policeLetters} onChange={setPoliceLetters}/>

      {/* City Taxi Permits (multi) */}
      <PermitsCard permits={permits} onChange={setPermits}/>
    </div>
  );
}

function PermitsCard({ permits, onChange }) {
  const [draft, setDraft] = useState({ permit_number: "", city: "", valid_from: "", valid_to: "", notes: "", file_id: null, file_url: null });
  const [draftOpen, setDraftOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const draftFileRef = useRef(null);

  const pickDraftFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const meta = await uploadDriverDocFull(f);
      setDraft(d => ({ ...d, file_id: meta.file_id, file_url: meta.full_url }));
      toast.success("Permit photo selected · click ADD PERMIT to save");
    } catch (err) { toast.error(err.message || "Upload failed"); }
  };

  const addPermit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/internal-docs/permits", {
        file_id: draft.file_id, file_url: draft.file_url,
        permit_number: draft.permit_number, city: draft.city,
        valid_from: draft.valid_from || null, valid_to: draft.valid_to || null,
        notes: draft.notes,
      });
      onChange([...(permits || []), data.permit]);
      setDraft({ permit_number: "", city: "", valid_from: "", valid_to: "", notes: "", file_id: null, file_url: null });
      setDraftOpen(false);
      toast.success("Permit added ✓");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add permit"); }
    finally { setBusy(false); }
  };

  const removePermit = async (permit_id) => {
    if (!window.confirm("Remove this permit?")) return;
    try {
      await api.delete(`/driver/internal-docs/permits/${permit_id}`);
      onChange((permits || []).filter(p => p.id !== permit_id));
      toast.success("Permit removed");
    } catch (err) { toast.error("Delete failed"); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="permits-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold flex items-center gap-2"><FileBadge2 className="h-4 w-4 text-amber-400"/>City Taxi Permits</h3>
        <span className="text-[10px] text-zinc-500">{(permits || []).length} / 10</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">Each city's permit is filed separately. Add a new permit any time you renew or get authorized in another city.</p>

      <div className="space-y-2 mb-3">
        {(permits || []).map((p) => (
          <div key={p.id} className="flex items-center gap-2 rounded border border-zinc-800 bg-zinc-950 px-3 py-2" data-testid={`permit-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{p.permit_number || "—"} · {p.city || "—"}</div>
              <div className="text-[10px] text-zinc-500">{p.valid_from || "—"} → {p.valid_to || "—"}{p.notes ? " · " + p.notes : ""}</div>
            </div>
            {p.file_url && <Button size="sm" variant="ghost" onClick={() => window.open(p.file_url, "_blank")} className="text-amber-400 text-[10px]" data-testid={`permit-view-${p.id}`}>View</Button>}
            <Button size="sm" variant="ghost" onClick={() => removePermit(p.id)} className="text-red-400" data-testid={`permit-del-${p.id}`}><Trash2 className="h-3.5 w-3.5"/></Button>
          </div>
        ))}
        {(permits || []).length === 0 && <div className="text-[11px] text-zinc-600 italic">No permits added yet.</div>}
      </div>

      {!draftOpen && (permits || []).length < 10 && (
        <Button data-testid="add-permit-btn" onClick={() => setDraftOpen(true)} variant="outline" className="w-full bg-zinc-950 border-zinc-700 border-dashed">
          <Plus className="h-4 w-4 mr-1"/>Add New Permit
        </Button>
      )}

      {draftOpen && (
        <div className="border border-amber-400/30 rounded p-3 bg-amber-400/5 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-amber-400 font-bold">New permit</div>
          <Input data-testid="permit-number-input" placeholder="Permit number (e.g., 1800)" value={draft.permit_number} onChange={e=>setDraft({...draft, permit_number: e.target.value})} className="bg-zinc-950 border-zinc-700"/>
          <Input data-testid="permit-city-input" placeholder="City (e.g., Weatherford)" value={draft.city} onChange={e=>setDraft({...draft, city: e.target.value})} className="bg-zinc-950 border-zinc-700"/>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Valid from</Label>
              <Input data-testid="permit-from-input" type="date" value={draft.valid_from} onChange={e=>setDraft({...draft, valid_from: e.target.value})} className="bg-zinc-950 border-zinc-700"/>
            </div>
            <div>
              <Label className="text-[10px]">Valid to</Label>
              <Input data-testid="permit-to-input" type="date" value={draft.valid_to} onChange={e=>setDraft({...draft, valid_to: e.target.value})} className="bg-zinc-950 border-zinc-700"/>
            </div>
          </div>
          <Input data-testid="permit-notes-input" placeholder="Notes (optional)" value={draft.notes} onChange={e=>setDraft({...draft, notes: e.target.value})} className="bg-zinc-950 border-zinc-700"/>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={() => draftFileRef.current?.click()} variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" data-testid="permit-pick-file">
              <FileText className="h-3.5 w-3.5 mr-1"/>{draft.file_id ? "Replace photo" : "Add photo"}
            </Button>
            {draft.file_url && <span className="text-[10px] text-green-400 uppercase">Photo selected ✓</span>}
            <input ref={draftFileRef} type="file" accept="image/*,application/pdf" onChange={pickDraftFile} className="hidden" data-testid="permit-file-input"/>
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="permit-save-btn" onClick={addPermit} disabled={busy} className="flex-1 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-9">
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "ADD PERMIT"}
            </Button>
            <Button data-testid="permit-cancel-btn" onClick={() => { setDraftOpen(false); setDraft({ permit_number: "", city: "", valid_from: "", valid_to: "", notes: "", file_id: null, file_url: null }); }} variant="ghost" className="h-9">Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// =================================================================
// v2.6.9 batch U · Service Oklahoma Driving Record (max 4)
// =================================================================
function SokRecordsCard({ records, onChange }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [notes, setNotes] = useState("");
  const fileRef = useRef(null);
  const MAX = 4;

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (records.length >= MAX) { toast.error(`Maximum ${MAX} records`); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("comment", "sok-driving-record");
      const upr = await api.post("/uploads/driver-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const body = { file_id: upr.data.file_id || null, file_url: upr.data.url || null, notes: notes.trim() || null };
      const r = await api.post("/driver/internal-docs/sok-driving-records", body);
      onChange((prev) => [...prev, r.data.record]);
      setNotes(""); setPicking(false);
      toast.success("Service Oklahoma record added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Service Oklahoma record?")) return;
    try {
      await api.delete(`/driver/internal-docs/sok-driving-records/${id}`);
      onChange((prev) => prev.filter((r) => r.id !== id));
      toast.success("Record deleted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="sok-records-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold flex items-center gap-2"><FileBadge2 className="h-4 w-4 text-cyan-400"/>Service Oklahoma Driving Record Verification</h3>
        <span className="text-[10px] text-zinc-500">{records.length}/{MAX}</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">Driving record obtained from Service Oklahoma (formerly OK DPS / DMV). Up to {MAX} uploads · 100&nbsp;MB max per file.</p>
      <div className="space-y-2 mb-3">
        {records.length === 0 && <p className="text-[11px] text-zinc-600 italic">No records uploaded yet.</p>}
        {records.map((r) => (
          <div key={r.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 flex items-center gap-2" data-testid={`sok-row-${r.id}`}>
            <FileText className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-xs">Uploaded {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString() : "—"}</div>
              {r.notes && <div className="text-[10px] text-zinc-500 truncate">{r.notes}</div>}
            </div>
            {r.file_url && <Button size="sm" variant="ghost" onClick={() => window.open(r.file_url, "_blank")} className="text-amber-400 text-[10px] h-7" data-testid={`sok-view-${r.id}`}>View</Button>}
            <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-400 text-[10px] h-7" data-testid={`sok-del-${r.id}`}>Delete</Button>
          </div>
        ))}
      </div>
      {!picking && records.length < MAX && (
        <Button onClick={() => setPicking(true)} variant="outline" className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 w-full" data-testid="sok-add-btn">
          <Plus className="h-4 w-4 mr-1"/>Add Driving Record
        </Button>
      )}
      {picking && (
        <div className="space-y-2 border border-cyan-500/30 rounded p-3 bg-cyan-500/5">
          <div>
            <Label className="text-[10px] text-zinc-400">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., 2026 renewal" className="bg-zinc-950 border-zinc-700 h-8 mt-1" data-testid="sok-notes-input"/>
          </div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={handlePick} className="hidden" data-testid="sok-file-input"/>
          <div className="flex gap-2">
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="flex-1 bg-cyan-400 hover:bg-cyan-300 text-zinc-950 font-bold h-9" data-testid="sok-pick-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Choose File & Upload"}
            </Button>
            <Button variant="ghost" onClick={() => { setPicking(false); setNotes(""); }} className="h-9">Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// =================================================================
// v2.6.9 batch U · Police Department Authorization Letters (max 10)
// =================================================================
function PoliceAuthCard({ letters, onChange }) {
  const [busy, setBusy] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draft, setDraft] = useState({ city: "", state: "", department: "", notes: "", file_id: null, file_url: null });
  const fileRef = useRef(null);
  const MAX = 10;

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("comment", "police-auth-letter");
      const upr = await api.post("/uploads/driver-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, file_id: upr.data.file_id || null, file_url: upr.data.url || null }));
      toast.success("File uploaded · fill out city/state/department and Save");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!draft.file_id && !draft.file_url) { toast.error("Upload a letter file first"); return; }
    if (!draft.department.trim()) { toast.error("Department is required"); return; }
    if (!draft.city.trim() || !draft.state.trim()) { toast.error("City and State are required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/driver/internal-docs/police-auth-letters", draft);
      onChange((prev) => [...prev, r.data.letter]);
      setDraft({ city: "", state: "", department: "", notes: "", file_id: null, file_url: null });
      setDraftOpen(false);
      toast.success("Police authorization letter added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this police authorization letter?")) return;
    try {
      await api.delete(`/driver/internal-docs/police-auth-letters/${id}`);
      onChange((prev) => prev.filter((letter) => letter.id !== id));
      toast.success("Letter deleted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="police-auth-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-400"/>Police Department Authorization Letter</h3>
        <span className="text-[10px] text-zinc-500">{letters.length}/{MAX}</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">Authorization letter(s) issued by city/county police departments allowing taxi operation in their jurisdiction. 100&nbsp;MB max per file.</p>
      <div className="space-y-2 mb-3">
        {letters.length === 0 && <p className="text-[11px] text-zinc-600 italic">No authorization letters uploaded yet.</p>}
        {letters.map((letter) => (
          <div key={letter.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 flex items-center gap-2" data-testid={`police-row-${letter.id}`}>
            <ShieldCheck className="h-3.5 w-3.5 text-blue-400 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">{letter.department || "—"}</div>
              <div className="text-[10px] text-zinc-500">{letter.city || "—"}, {letter.state || "—"} · Uploaded {letter.uploaded_at ? new Date(letter.uploaded_at).toLocaleDateString() : "—"}</div>
              {letter.notes && <div className="text-[10px] text-zinc-500 truncate">{letter.notes}</div>}
            </div>
            {letter.file_url && <Button size="sm" variant="ghost" onClick={() => window.open(letter.file_url, "_blank")} className="text-amber-400 text-[10px] h-7" data-testid={`police-view-${letter.id}`}>View</Button>}
            <Button size="sm" variant="ghost" onClick={() => remove(letter.id)} className="text-red-400 text-[10px] h-7" data-testid={`police-del-${letter.id}`}>Delete</Button>
          </div>
        ))}
      </div>
      {!draftOpen && letters.length < MAX && (
        <Button onClick={() => setDraftOpen(true)} variant="outline" className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 w-full" data-testid="police-add-btn">
          <Plus className="h-4 w-4 mr-1"/>Add Authorization Letter
        </Button>
      )}
      {draftOpen && (
        <div className="space-y-2 border border-blue-500/30 rounded p-3 bg-blue-500/5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-400">City *</Label>
              <Input value={draft.city} onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))} placeholder="e.g., Weatherford" className="bg-zinc-950 border-zinc-700 h-8 mt-1" data-testid="police-city-input"/>
            </div>
            <div>
              <Label className="text-[10px] text-zinc-400">State *</Label>
              <Input value={draft.state} onChange={(e) => setDraft((d) => ({ ...d, state: e.target.value.toUpperCase().slice(0, 2) }))} placeholder="OK" maxLength={2} className="bg-zinc-950 border-zinc-700 h-8 mt-1 uppercase" data-testid="police-state-input"/>
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-zinc-400">Department *</Label>
            <Input value={draft.department} onChange={(e) => setDraft((d) => ({ ...d, department: e.target.value }))} placeholder="e.g., Weatherford Police Department" className="bg-zinc-950 border-zinc-700 h-8 mt-1" data-testid="police-dept-input"/>
          </div>
          <div>
            <Label className="text-[10px] text-zinc-400">Notes (optional)</Label>
            <Input value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="e.g., Authorized 2026-2027" className="bg-zinc-950 border-zinc-700 h-8 mt-1" data-testid="police-notes-input"/>
          </div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={pickFile} className="hidden" data-testid="police-file-input"/>
          <Button onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="bg-zinc-950 border-zinc-700 w-full h-9" data-testid="police-pick-btn">
            <FileText className="h-4 w-4 mr-1"/>{draft.file_url ? "Replace File" : "Upload Letter File"}{draft.file_url && " ✓"}
          </Button>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={busy} className="flex-1 bg-blue-400 hover:bg-blue-300 text-zinc-950 font-bold h-9" data-testid="police-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "ADD LETTER"}
            </Button>
            <Button variant="ghost" onClick={() => { setDraftOpen(false); setDraft({ city: "", state: "", department: "", notes: "", file_id: null, file_url: null }); }} className="h-9">Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function DocUpload({ label, current, onPick, clear, fileRef, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <Button type="button" data-testid={`${testid}-pick`} onClick={() => fileRef.current?.click()} variant="outline" className="bg-zinc-950 border-zinc-700">
          <FileText className="h-4 w-4 mr-1"/>{current ? "Replace" : "Upload"}
        </Button>
        {current && <Button type="button" data-testid={`${testid}-view`} variant="ghost" onClick={() => window.open(current, "_blank")} className="text-amber-400 text-xs">View current</Button>}
        {current && <Button type="button" data-testid={`${testid}-clear`} variant="ghost" onClick={clear} className="text-red-400 text-xs">Clear</Button>}
      </div>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onPick} className="hidden" data-testid={testid}/>
    </div>
  );
}

function AdminAccessSection() {
  return (
    <div className="space-y-4">
      <CredentialsCard/>
      <AdminPasscodeCard/>
    </div>
  );
}

function CredentialsCard() {
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPwd = async () => {
    if (!curPwd || !newPwd) return toast.error("Current and new password required");
    if (newPwd.length < 6) return toast.error("New password must be 6+ chars");
    setBusy(true);
    try {
      await api.post("/auth/me/credentials", { current_password: curPwd, new_password: newPwd });
      toast.success("Password updated");
      setCurPwd(""); setNewPwd("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const submitPin = async () => {
    if (!/^\d{4,8}$/.test(newPin)) return toast.error("PIN must be 4–8 digits");
    setBusy(true);
    try {
      await api.post("/auth/me/credentials", { new_pin: newPin });
      toast.success("PIN updated");
      setNewPin("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="self-credentials-card">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="h-4 w-4 text-amber-400"/>
        <h3 className="font-bold">My Login Credentials</h3>
      </div>
      <p className="text-xs text-zinc-400 mb-4">Update your account login password (used on the dispatch / driver email tab) and your 4–8 digit PIN.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Change Password</Label>
          <Input data-testid="cred-cur-pwd" type="password" placeholder="Current password" value={curPwd} onChange={e=>setCurPwd(e.target.value)} className="bg-zinc-950 border-zinc-700 h-10 font-mono"/>
          <Input data-testid="cred-new-pwd" type="password" placeholder="New password (6+ chars)" value={newPwd} onChange={e=>setNewPwd(e.target.value)} className="bg-zinc-950 border-zinc-700 h-10 font-mono"/>
          <Button data-testid="cred-save-pwd" onClick={submitPwd} disabled={busy || !curPwd || !newPwd} className="w-full h-10 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Update Password"}
          </Button>
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Change PIN</Label>
          <Input data-testid="cred-new-pin" type="password" inputMode="numeric" placeholder="New PIN (4–8 digits)" value={newPin} onChange={e=>setNewPin(e.target.value.replace(/[^0-9]/g, ""))} maxLength={8} className="bg-zinc-950 border-zinc-700 h-10 font-mono"/>
          <p className="text-[10px] text-zinc-500">PIN reset doesn't require old PIN since you're already signed in.</p>
          <Button data-testid="cred-save-pin" onClick={submitPin} disabled={busy || !newPin} className="w-full h-10 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Update PIN"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function AdminPasscodeCard() {
  const [hasCode, setHasCode] = useState(null);
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me/admin-passcode").then(r => setHasCode(r.data.has_admin_passcode)).catch(()=>{});
  }, []);

  const save = async () => {
    if (code !== confirm) return toast.error("Passcodes don't match");
    if (code && (code.length < 4 || code.length > 12)) return toast.error("Passcode must be 4–12 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/me/admin-passcode", { passcode: code });
      setHasCode(data.has_admin_passcode);
      setCode(""); setConfirm("");
      toast.success(data.has_admin_passcode ? "Admin passcode saved" : "Admin passcode cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const clearCode = async () => {
    if (!window.confirm("Remove your admin passcode?")) return;
    setBusy(true);
    try {
      await api.post("/auth/me/admin-passcode", { passcode: "" });
      setHasCode(false); setCode(""); setConfirm("");
      toast.success("Admin passcode removed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="admin-passcode-card">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="h-4 w-4 text-amber-400"/>
        <h3 className="font-bold">Admin Passcode</h3>
        {hasCode === true && <span className="ml-auto text-[10px] uppercase tracking-widest text-green-400 border border-green-400/40 rounded-full px-2 py-0.5">Active</span>}
        {hasCode === false && <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-700 rounded-full px-2 py-0.5">Not set</span>}
      </div>
      <p className="text-xs text-zinc-400 mb-4">
        Set a personal passcode (4–12 characters) so you can sign into the Admin Console
        from the Login → <span className="text-amber-400 font-semibold">Dispatch</span> tab → PIN sub-tab.
        You'll get <span className="text-amber-400 font-semibold">dispatcher-level</span> access — manage rides,
        drivers, customers, and dispatch — but not Payments, Pricing, Legal pages, or supervisor accounts.
      </p>

      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400">{hasCode ? "New passcode" : "Passcode"}</Label>
          <Input data-testid="admin-passcode-new" type="password" value={code} onChange={e=>setCode(e.target.value)} placeholder="4–12 characters" className="bg-zinc-950 border-zinc-700 h-10 mt-1 font-mono"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Confirm</Label>
          <Input data-testid="admin-passcode-confirm" type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Re-enter to confirm" className="bg-zinc-950 border-zinc-700 h-10 mt-1 font-mono"/>
        </div>
        <div className="flex gap-2">
          <Button data-testid="admin-passcode-save" onClick={save} disabled={busy || !code || code !== confirm} className="flex-1 h-11 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : (hasCode ? "Update Passcode" : "Save Passcode")}
          </Button>
          {hasCode && <Button data-testid="admin-passcode-clear" type="button" variant="outline" onClick={clearCode} disabled={busy} className="bg-zinc-950 border-red-400/40 text-red-400 hover:bg-red-500/10">Remove</Button>}
        </div>
      </div>
    </Card>
  );
}

